import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {useState, useEffect} from "react";
import {BsFilter, BsSearch} from "react-icons/bs";
import {TableData} from "../../../components/TableData";
import {StatusCard} from "../../../components/StatusCard";
import {useLocation, useNavigate} from "react-router-dom";
import {EmptyState} from "../../../components/EmptyState";
import axios from "axios";
import {useQuery} from "react-query";
import {getStatusType} from "../../../utils/status";
import moment from "moment";

const TABLE_HEAD = [
  "Created at",
  "Sender",
  "Order ID",
  "Pickup Address",
  "City",
  "Pickup Date",
  "Phone number",
  "Pickups Type",
  "Status",
];

const PICKUP_TYPE_OPTIONS = [
  {label: "All", value: ""},
  {label: "Standard", value: "STANDARD"},
  {label: "Express", value: "EXPRESS"},
  {label: "Same Day", value: "SAME_DAY"},
];

export const AssignedPickupsTable = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("Filter");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pickupTypeFilter, setPickupTypeFilter] = useState("");

  // Get current page from URL or default to 1
  const queryParams = new URLSearchParams(location.search);
  const currentPage = Number(queryParams.get("page")) || 1;

  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  const getPickups = async () => {
    let url = `${Baseurl}/api/admin/orders/pickups?limit=${limit}&page=${currentPage}&filter=status:ASSIGNED&sort=pickupDate:DESC`;

    if (debouncedSearchQuery) {
      url += `&query=${encodeURIComponent(debouncedSearchQuery)}`;
    }

    if (pickupTypeFilter) {
      url += `&filter=deliveryType:${pickupTypeFilter}`;
    }

    const response = await axios.get(url, config);
    setTotalPages(Math.ceil(response?.data?.data?.meta?.total / limit));
    return response?.data?.data?.items;
  };

  const {
    data: pickupsData,
    isLoading: isLoadingPickupsData,
    refetch,
    isRefetching: isRefetchingPickupsData,
  } = useQuery(
    [
      "ASSIGNED_PICKUPS",
      currentPage,
      limit,
      debouncedSearchQuery,
      pickupTypeFilter,
    ],
    getPickups,
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    refetch();
  }, [debouncedSearchQuery, pickupTypeFilter, currentPage, limit, refetch]);

  const handlePageChange = (newPage: number) => {
    navigate(`?page=${newPage}`);
  };

  const handlePickupTypeFilter = (value: string, label: string) => {
    setPickupTypeFilter(value);
    setSelectedLabel(label === "All" ? "Filter" : label);
    handlePageChange(1); // Reset to first page when filter changes
  };

  const containerStyle = {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #e4e4e7",
  };

  const tableRowStyle = {
    borderBottom: "1px solid #eaecf0",
  };

  return (
    <div style={containerStyle}>
      <div className="flex items-center justify-between !px-6 !py-4">
        <h3 className="!text-xl !font-bold text-gray-700">Assigned Pickups</h3>
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative !h-12 !bg-gray-50 !border !rounded-[10px] !w-[428px] !border-gray-300">
            <input
              type="text"
              placeholder="Search assigned pickups"
              className="w-full h-full focus:!outline-none !pl-14 placeholder:!text-black/30 !text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="!absolute !top-3.5 !left-6">
              <BsSearch />
            </span>
          </div>

          {/* Filter dropdown */}
          <Menu as="div" className="relative">
            <MenuButton className="flex items-center gap-2 py-3 px-3 text-sm font-medium border rounded-lg border-gray-300 text-gray-600">
              <BsFilter />
              <span>{selectedLabel}</span>
            </MenuButton>
            <MenuItems className="absolute right-0 z-10 w-48 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
              {PICKUP_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value}>
                  {({active}) => (
                    <button
                      className={`block w-full p-2 text-left text-sm ${
                        active ? "bg-gray-100" : ""
                      }`}
                      onClick={() =>
                        handlePickupTypeFilter(option.value, option.label)
                      }
                    >
                      {option.label}
                    </button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        </div>
      </div>

      {isLoadingPickupsData || isRefetchingPickupsData ? (
        <div style={{padding: "56px 0"}}>
          <p>Loading...</p>
        </div>
      ) : pickupsData?.length > 0 ? (
        <>
          <div className="!overflow-auto">
            <table className="!w-full !min-w-max !table-auto !text-left">
              <thead>
                <tr className="!border-b !border-b-[#eaecf0] !bg-[#f9fafb]">
                  {TABLE_HEAD.map((head) => (
                    <th
                      key={head}
                      className="!text-xs !font-medium !text-[#667085] !p-3"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pickupsData.map((order: any) => {
                  const status = getStatusType(order.status);
                  const formattedDate = new Date(
                    order.pickupDate
                  ).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  });

                  return (
                    <tr
                      key={order.id}
                      style={tableRowStyle}
                      className="border-b border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      <TableData
                        content={
                          order.createdAt ? (
                            <>
                              {moment(order.createdAt).format("DD MMM, YYYY")}
                              <br />
                              {moment(order.createdAt).format("hh:mmA")}
                            </>
                          ) : (
                            "N/A"
                          )
                        }
                      />

                      <TableData content={order.senderName} />
                      <TableData content={order.orderId} />
                      <td className="!text-[13px] !font-medium !text-gray-600 !py-5 !px-3 text-wrap max-w-[200px]">
                        {`${order.address}`}
                      </td>
                      <TableData
                        content={
                          order.city && order.city !== "" ? order.city : "N/A"
                        }
                      />
                      <TableData content={formattedDate} />
                      <TableData content={order.phone} />
                      <TableData content={order.deliveryType} />
                      <td style={{padding: "20px 12px"}}>
                        <StatusCard title={status.title} type={status.type} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <p className="text-sm text-gray-500">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Items per page:</p>
              <select
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  handlePageChange(1);
                }}
                className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
                value={limit}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div style={{padding: "56px 0"}}>
          <EmptyState type="order" text="No Assigned Pickups Found" />
        </div>
      )}
    </div>
  );
};
